import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, MapPin, CheckCircle, AlertCircle, Loader, Clock } from 'lucide-react';
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_URL = API_CONFIG.BASE_URL + '/api';

interface Region {
  id: number;
  name: string;
  country: string;
}

interface UpdateStatus {
  region_id: number;
  region_name: string;
  status: 'idle' | 'updating' | 'success' | 'error';
  message?: string;
  routes_generated?: number;
  start_time?: number;
  elapsed_time?: number;
}

interface CacheStats {
  region_id: number;
  total_routes: number;
  unique_from_stations: number;
  unique_to_stations: number;
  multi_company_routes: number;
  single_company_routes: number;
  avg_cost: number;
  avg_stations: number;
  last_generated: string | null;
}

const RouteUpdateManager: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [updateStatuses, setUpdateStatuses] = useState<Record<number, UpdateStatus>>({});
  // Cache stats endpoint has been removed - keeping empty object for now
  const cacheStats: Record<number, CacheStats> = {};

  const fetchRegions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/regions/`);
      // Handle both array response and object with regions property
      const regionsData = Array.isArray(response.data) ? response.data : response.data.regions || [];
      setRegions(regionsData);

      // Initialize statuses
      const statuses: Record<number, UpdateStatus> = {};
      regionsData.forEach((region: Region) => {
        statuses[region.id] = {
          region_id: region.id,
          region_name: region.name,
          status: 'idle'
        };
      });
      setUpdateStatuses(statuses);

      // Stats endpoint has been removed - no longer fetching cache stats
    } catch (error) {
      console.error('Error fetching regions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const updateRoutes = async (regionId: number) => {
    const startTime = Date.now();

    // Update status to updating
    setUpdateStatuses(prev => ({
      ...prev,
      [regionId]: {
        ...prev[regionId],
        status: 'updating',
        message: 'Generating pre-computed routes for all companies in region...',
        start_time: startTime,
        elapsed_time: 0
      }
    }));

    // Start timer to update elapsed time
    const timer = setInterval(() => {
      setUpdateStatuses(prev => {
        if (prev[regionId]?.status === 'updating') {
          return {
            ...prev,
            [regionId]: {
              ...prev[regionId],
              elapsed_time: Math.floor((Date.now() - startTime) / 1000)
            }
          };
        }
        return prev;
      });
    }, 1000);

    try {
      const response = await axios.post(`${API_URL}/route-cache/generate`, {
        region_id: regionId
      });

      clearInterval(timer);

      setUpdateStatuses(prev => ({
        ...prev,
        [regionId]: {
          ...prev[regionId],
          status: 'success',
          message: `Successfully generated ${response.data.routes_generated || 0} routes in ${Math.floor((Date.now() - startTime) / 1000)}s`,
          routes_generated: response.data.routes_generated,
          elapsed_time: Math.floor((Date.now() - startTime) / 1000)
        }
      }));

      // Stats endpoint has been removed - no longer refreshing cache stats
    } catch (error: any) {
      clearInterval(timer);
      setUpdateStatuses(prev => ({
        ...prev,
        [regionId]: {
          ...prev[regionId],
          status: 'error',
          message: error.response?.data?.detail || 'Failed to update routes',
          elapsed_time: Math.floor((Date.now() - startTime) / 1000)
        }
      }));
    }
  };

  const updateAllRoutes = async () => {
    for (const region of regions) {
      await updateRoutes(region.id);
      // Small delay between regions
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin h-8 w-8 text-admin-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Update Routes Cache</h2>
          <p className="text-gray-600 mt-1">
            Regenerate pre-computed routes when stations or pricing changes
          </p>
        </div>
        <button
          onClick={updateAllRoutes}
          disabled={Object.values(updateStatuses).some(s => s.status === 'updating')}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            Object.values(updateStatuses).some(s => s.status === 'updating')
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-admin-600 text-white hover:bg-admin-700'
          }`}
        >
          <RefreshCw className={`h-5 w-5 ${Object.values(updateStatuses).some(s => s.status === 'updating') ? 'animate-spin' : ''}`} />
          <span>Update All Regions</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="space-y-4">
            {regions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>No regions found</p>
              </div>
            ) : (
              regions.map((region) => {
                const status = updateStatuses[region.id];
                const stats = cacheStats[region.id];
                return (
                  <div
                    key={region.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center space-x-4 flex-1">
                      <MapPin className="h-8 w-8 text-admin-600" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{region.name}</h3>
                        <p className="text-sm text-gray-600">{region.country}</p>

                        {/* Cache Stats */}
                        {stats && (
                          <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                            <span>Total Routes: {stats.total_routes}</span>
                            <span>Single-Company: {stats.single_company_routes}</span>
                            <span>Multi-Company: {stats.multi_company_routes}</span>
                            {stats.last_generated && (
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {new Date(stats.last_generated).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Update Status */}
                        {status?.message && (
                          <p className={`text-sm mt-1 ${
                            status.status === 'success' ? 'text-green-600' :
                            status.status === 'error' ? 'text-red-600' :
                            'text-blue-600'
                          }`}>
                            {status.message}
                            {status.status === 'updating' && status.elapsed_time !== undefined && (
                              <span className="ml-2">({status.elapsed_time}s elapsed)</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {status?.status === 'updating' && (
                        <Loader className="animate-spin h-5 w-5 text-admin-600" />
                      )}
                      {status?.status === 'success' && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                      {status?.status === 'error' && (
                        <AlertCircle className="h-5 w-5 text-red-600" />
                      )}

                      <button
                        onClick={() => updateRoutes(region.id)}
                        disabled={status?.status === 'updating'}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          status?.status === 'updating'
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-admin-600 text-white hover:bg-admin-700'
                        }`}
                      >
                        {status?.status === 'updating' ? 'Updating...' : 'Update Routes'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">How It Works</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Updates pre-computed routes cache for faster pathfinding</li>
          <li>• Runs pathfinding for all station pairs across ALL companies in each region</li>
          <li>• Includes fare breakdowns for multi-company routes with segment-by-segment pricing</li>
          <li>• Stores route data, prices, interchange information, and transfer details</li>
          <li>• Run this after adding/modifying stations, updating pricing, or changing routes</li>
        </ul>
      </div>
    </div>
  );
};

export default RouteUpdateManager;
